import { createServerFn } from "@tanstack/react-start";

export type PseoPage = {
  slug: string;
  heroHeadline: string;
  heroSubheadline: string | null;
  localContext: string | null;
  technicalExplanation: string | null;
  ctaText: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  ogImage: string | null;
  updatedAt: string | null;
};

function flattenBlocks(value: unknown): string | null {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    try {
      return (value as Array<{ children?: Array<{ text?: string }> }>)
        .map((b) => (b.children ?? []).map((c) => c.text ?? "").join(""))
        .join("\n\n");
    } catch {
      return null;
    }
  }
  return null;
}

function s(value: unknown): string | null {
  return typeof value === "string" ? value : flattenBlocks(value);
}

function pickImageUrl(value: unknown, baseUrl: string): string | null {
  if (!value || typeof value !== "object") return null;
  // Strapi v4: { data: { attributes: { url, formats } } } or { data: [{...}] }
  // Strapi v5: { url, formats }
  const v = value as Record<string, unknown>;
  const node =
    (v.data && typeof v.data === "object"
      ? Array.isArray(v.data)
        ? (v.data[0] as Record<string, unknown> | undefined)
        : (v.data as Record<string, unknown>)
      : v) ?? null;
  if (!node) return null;
  const attrs =
    ((node as Record<string, unknown>).attributes as
      | Record<string, unknown>
      | undefined) ?? (node as Record<string, unknown>);
  const formats = attrs.formats as Record<string, { url?: string }> | undefined;
  const url =
    (formats?.large?.url as string | undefined) ??
    (formats?.medium?.url as string | undefined) ??
    (attrs.url as string | undefined);
  if (!url) return null;
  return url.startsWith("http") ? url : `${baseUrl.replace(/\/$/, "")}${url}`;
}

function normalize(raw: Record<string, unknown>, baseUrl: string): PseoPage {
  const attrs =
    (raw.attributes as Record<string, unknown> | undefined) ?? raw;
  const imageField =
    attrs.OGImage ?? attrs.OgImage ?? attrs.HeroImage ?? attrs.Image ?? attrs.image;
  return {
    slug: (attrs.Slug as string) ?? (attrs.slug as string) ?? "",
    heroHeadline: s(attrs.HeroHeadline) ?? "",
    heroSubheadline: s(attrs.HeroSubheadline),
    localContext: s(attrs.LocalContext),
    technicalExplanation: s(attrs.TechnicalExplanation),
    ctaText: (attrs.CTAText as string) ?? null,
    metaTitle: (attrs.MetaTitle as string) ?? null,
    metaDescription: (attrs.MetaDescription as string) ?? null,
    ogImage: pickImageUrl(imageField, baseUrl),
    updatedAt: (attrs.updatedAt as string) ?? null,
  };
}

export const fetchPseoPage = createServerFn({ method: "GET" })
  .inputValidator((input: { slug: string }) => {
    if (!input?.slug || typeof input.slug !== "string") {
      throw new Error("slug is required");
    }
    return { slug: input.slug };
  })
  .handler(async ({ data }) => {
    const baseUrl = process.env.STRAPI_URL;
    const token = process.env.STRAPI_API_TOKEN;
    const tenantId = process.env.TENANT_ID;

    if (!baseUrl || !token || !tenantId) {
      throw new Error(
        "Strapi env vars missing: STRAPI_URL, STRAPI_API_TOKEN, TENANT_ID",
      );
    }

    const url = new URL("/api/pseo-pages", baseUrl);
    url.searchParams.set("filters[Slug][$eq]", data.slug);
    url.searchParams.set("filters[client_tenant][TenantID][$eq]", tenantId);
    url.searchParams.set("populate", "*");

    const res = await fetch(url.toString(), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Strapi request failed: ${res.status}`);

    const json = (await res.json()) as { data?: Array<Record<string, unknown>> };
    const entry = json.data?.[0];
    return entry ? normalize(entry, baseUrl) : null;
  });
